/**
 * The "Server version & updates" card on the Settings page: which build of the CRM server is
 * running, the builds installed beside it, what the update feed offers, switching between them, and
 * going back.
 *
 *   * ADMIN ONLY, and hidden rather than disabled for anyone else: every endpoint behind it is
 *     admin-only, and choosing what runs as the CRM is not a technician's call.
 *   * GOING BACK IS A ROW BUTTON, not a buried option. Every build that was active stays installed
 *     until it falls out of the keep count, and the packaged build is always there. Ticking "also put
 *     back the database" restores the copy taken when the server last moved off that build.
 *   * A SWITCH RESTARTS THE SERVER. The card waits for it to come back on the new build, then reloads
 *     the page so the browser is running that build's interface too.
 *   * THE SAME CONTROLS LIVE IN DSM (Jetsonsoft CRM Settings), for when the build that is running is
 *     the thing that is broken and this page will not load. The card says so.
 */
import { ApiError } from '../../api/auth.js';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import type {
  ServerReleaseDto,
  ServerSnapshotDto,
  ServerUpdateActionResult,
  ServerUpdateStatusDto,
  ServerVersionDto
} from '../../contracts.gen.js';
import { pickFiles, uploadFiles } from '../../download.js';
import { bytes, join, localDateTime } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  field,
  fieldRow,
  FONT,
  GAP,
  h2,
  muted,
  relayout,
  stackOf,
  text,
  textBox,
  value,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { PageContext } from '../page.js';

export interface ServerUpdateSection {
  card: Card;
  load(): Promise<void>;
}

const PACKAGED = 'packaged';

function whole(input: string): number | null {
  const trimmed = input.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** Newer first when negative. */
function compareVersions(a: string, b: string): number {
  const x = a.split('.').map(Number);
  const y = b.split('.').map(Number);
  for (let i = 0; i < 4; i++) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

export function serverUpdateSection(ctx: PageContext): ServerUpdateSection {
  let current: ServerUpdateStatusDto | null = null;
  let busy = false;

  const status = new StatusLine();
  const versionStatus = new StatusLine();

  const unmanaged = muted('');
  const runningLine = text('', { font: FONT.bold });
  const latestLine = muted('');
  const busyLine = muted('');
  const failureBanner = card({ tint: 'danger', visible: false });
  const failureText = text('');
  failureBanner.controls.add(failureText);

  const updateButton = button('Update now', () => void install(null, true), true);
  const checkButton = button('Check for updates', () => void check());
  const uploadButton = button('Upload a build…', () => void upload());

  // ---- Builds on this server ----
  const restoreDatabase = wrappingCheck('When switching to another build, also put back the database as that build last left it');
  const versions = new CardList<ServerVersionDto>({
    key: v => `${v.packaged ? PACKAGED : v.version}`,
    version: v => [v, busy, current?.runningVersion],
    build: v => buildVersionRow(v),
    spacing: GAP.xs
  });

  // ---- Offered by the feed ----
  const availableHeading = text('Available from the update feed', { font: FONT.bold });
  const available = new CardList<ServerReleaseDto>({
    key: r => r.version,
    version: r => [r, busy, current?.runningVersion],
    build: r => buildReleaseRow(r),
    spacing: GAP.xs
  });

  // ---- Settings ----
  const autoUpdate = wrappingCheck('Install new builds automatically');
  const feedBox = textBox({ placeholder: 'https://downloads.example.com/jetsoncrm/feed.json' });
  const hoursBox = textBox({ text: '6' });
  const keepBox = textBox({ text: '3' });
  const skippedLine = muted('');
  const clearSkippedButton = button('Allow skipped builds again', () => void saveConfig(true));
  const saveButton = button('Save update settings', () => void saveConfig(false), true);
  const configStatus = new StatusLine();

  // ---- Database copies ----
  const snapshotsHeading = text('Database copies', { font: FONT.bold });
  const snapshotsNote = muted(
    'Taken by each build at startup, before it opens the database, whenever the build changed. The newest ten are kept.'
  );
  const snapshots = new CardList<ServerSnapshotDto>({
    key: s => s.name,
    build: s => buildSnapshotRow(s),
    spacing: GAP.xs
  });

  const managedBody = stackOf(
    vstack({ spacing: GAP.md }),
    runningLine,
    latestLine,
    busyLine,
    failureBanner,
    stackOf(buttonRow(), updateButton, checkButton, uploadButton),
    status.label,

    text('Builds on this server', { font: FONT.bold }),
    muted(
      'The packaged build came with the installed DSM package and is always here. A switch restarts the server onto the chosen build, which takes a few seconds; the database is copied first, every time. A build that fails to start three times is rolled back automatically.'
    ),
    checkLine(restoreDatabase),
    muted('Everything recorded since that copy is lost. The database being replaced is kept as a copy too.'),
    versions.panel,
    versionStatus.label,

    availableHeading,
    available.panel,

    text('Automatic updates', { font: FONT.bold }),
    checkLine(autoUpdate),
    field('Update feed', feedBox),
    muted(
      'The feed.json published beside the builds (the package build writes it to bin\\Release\\net10.0\\updates). Upload that folder to any web host.'
    ),
    fieldRow(field('Check every (hours)', hoursBox), field('Builds to keep', keepBox)),
    skippedLine,
    stackOf(buttonRow(), saveButton, clearSkippedButton),
    configStatus.label,

    snapshotsHeading,
    snapshotsNote,
    snapshots.panel
  );

  const shell = card();
  shell.controls.add(
    stackOf(
      vstack({ spacing: GAP.md }),
      h2('Server version & updates'),
      muted(
        'The same controls are in DSM, under Jetsonsoft CRM Settings › Versions & updates - the way back if a build ever stops this page from loading.'
      ),
      unmanaged,
      managedBody
    )
  );

  function setBusy(on: boolean): void {
    busy = on;
    for (const b of [updateButton, checkButton, uploadButton, saveButton, clearSkippedButton]) b.enabled = !on;
    versions.set(current?.versions ?? []);
    available.set(current?.available ?? []);
  }

  function buildVersionRow(v: ServerVersionDto): Card {
    const name = v.packaged ? `${v.version} (packaged)` : v.version;
    const chips = stackOf(buttonRow({ spacing: GAP.xs }), value(name, { font: FONT.bold }));
    if (v.active) chips.controls.add(chip('active', 'ok'));
    if (v.running) chips.controls.add(chip('running', 'brand'));
    if (v.bad) chips.controls.add(chip('failed to start', 'danger'));
    if (v.skipped) chips.controls.add(chip('skipped by updates', 'warn'));

    const details = muted(
      join([
        v.packaged ? 'Came with the DSM package' : v.installedUtc ? `Installed ${localDateTime(v.installedUtc)}` : null,
        v.sizeBytes ? bytes(v.sizeBytes) : null,
        v.hasSnapshot ? 'database copy available' : null
      ])
    );

    const row = stackOf(vstack({ spacing: GAP.xs }), chips, details);
    const actions = stackOf(buttonRow());
    if (!v.active) {
      const older = current ? compareVersions(v.version, current.runningVersion) < 0 : false;
      const switchButton = button(older ? 'Go back to this build' : 'Switch to this build', () => void activate(v));
      switchButton.enabled = !busy;
      actions.controls.add(switchButton);
      if (!v.packaged && !v.running) {
        const removeButton = button('Remove', () => void remove(v));
        removeButton.enabled = !busy;
        actions.controls.add(removeButton);
      }
    }
    if (actions.controls.length > 0) row.controls.add(actions);

    const shellCard = card({ tint: v.running ? 'info' : 'alt' });
    shellCard.controls.add(row);
    return shellCard;
  }

  function buildReleaseRow(r: ServerReleaseDto): Card {
    const head = stackOf(buttonRow({ spacing: GAP.xs }), value(r.version, { font: FONT.bold }));
    if (current && compareVersions(r.version, current.runningVersion) > 0) head.controls.add(chip('newer', 'ok'));
    if (r.installed) head.controls.add(chip('installed', 'neutral'));
    const row = stackOf(
      vstack({ spacing: GAP.xs }),
      head,
      muted(join([r.publishedUtc ? `Published ${localDateTime(r.publishedUtc)}` : null, r.sizeBytes ? bytes(r.sizeBytes) : null]))
    );
    if (r.notes?.trim()) row.controls.add(text(r.notes.trim()));
    if (!r.installed && r.version !== current?.runningVersion) {
      const installSwitch = button('Install and switch', () => void install(r.version, true));
      const installOnly = button('Install only', () => void install(r.version, false));
      installSwitch.enabled = installOnly.enabled = !busy;
      row.controls.add(stackOf(buttonRow(), installSwitch, installOnly));
    }
    const shellCard = card({ tint: 'alt' });
    shellCard.controls.add(row);
    return shellCard;
  }

  function buildSnapshotRow(s: ServerSnapshotDto): Card {
    const shellCard = card({ tint: 'alt' });
    shellCard.controls.add(
      stackOf(
        vstack({ spacing: GAP.xs }),
        value(`${s.fromVersion} → ${s.toVersion}`, { font: FONT.bold }),
        muted(join([localDateTime(s.takenUtc), bytes(s.sizeBytes)]))
      )
    );
    return shellCard;
  }

  function bind(s: ServerUpdateStatusDto): void {
    current = s;
    unmanaged.visible = !s.managed;
    managedBody.visible = s.managed;
    if (!s.managed) {
      unmanaged.text = s.unmanagedReason ?? '';
      runningLine.text = '';
      relayout(shell);
      return;
    }

    const running = s.versions.find(v => v.running);
    runningLine.text = `Running build ${s.runningVersion}${running?.packaged ? ' (packaged)' : ''}.`;

    if (s.lastCheckError) {
      latestLine.text = s.lastCheckError;
      latestLine.role = 'danger';
    } else if (s.updateAvailable && s.latestVersion) {
      latestLine.text = `Build ${s.latestVersion} is available${s.lastCheckUtc ? ` (checked ${localDateTime(s.lastCheckUtc)})` : ''}.`;
      latestLine.role = 'ok';
    } else if (s.lastCheckUtc) {
      latestLine.text = `Up to date - checked the feed ${localDateTime(s.lastCheckUtc)}.`;
      latestLine.role = 'muted';
    } else {
      latestLine.text = s.config.feedUrl ? 'The feed has not been checked since the server started.' : 'No update feed is configured.';
      latestLine.role = 'muted';
    }
    updateButton.visible = s.updateAvailable;
    if (s.latestVersion) updateButton.text = `Update to ${s.latestVersion}`;

    busyLine.text = s.busy ?? '';
    busyLine.visible = !!s.busy;

    failureText.text = s.lastFailure ?? '';
    failureBanner.visible = !!s.lastFailure;

    versions.set(s.versions);
    availableHeading.visible = s.available.length > 0;
    available.set(s.available);

    autoUpdate.checked = s.config.autoUpdate;
    feedBox.text = s.config.feedUrl;
    hoursBox.text = String(s.config.checkHours);
    keepBox.text = String(s.config.keepVersions);
    skippedLine.text =
      s.config.skippedVersions.length > 0
        ? `Automatic updates skip ${s.config.skippedVersions.join(', ')}, because someone went back from ${s.config.skippedVersions.length === 1 ? 'it' : 'them'}.`
        : '';
    skippedLine.visible = s.config.skippedVersions.length > 0;
    clearSkippedButton.visible = s.config.skippedVersions.length > 0;

    snapshotsHeading.visible = snapshotsNote.visible = s.snapshots.length > 0;
    snapshots.set(s.snapshots);
    relayout(shell);
  }

  async function load(): Promise<void> {
    try {
      const s = await crm.getServerUpdateStatus(ctx.dispose.signal());
      if (!ctx.cancelled()) bind(s);
    } catch (error) {
      if (!ctx.cancelled()) status.error('Could not load the server version: ' + describeError(error));
    }
  }

  async function check(): Promise<void> {
    setBusy(true);
    status.busy('Checking the update feed…');
    try {
      const s = await crm.checkServerUpdates();
      if (ctx.cancelled()) return;
      bind(s);
      if (s.lastCheckError) status.error(s.lastCheckError);
      else status.ok(s.updateAvailable ? `Build ${s.latestVersion} is available.` : 'This server is up to date.');
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  /** Polls the install's progress line while a long download runs. */
  function watchProgress(): () => void {
    const timer = window.setInterval(() => {
      void crm.getServerUpdateStatus().then(s => {
        if (ctx.cancelled() || !s.busy) return;
        busyLine.text = s.busy;
        busyLine.visible = true;
        relayout(shell);
      }).catch(() => undefined);
    }, 1500);
    return () => window.clearInterval(timer);
  }

  async function install(version: string | null, activate: boolean): Promise<void> {
    const name = version ?? current?.latestVersion ?? 'the newest build';
    if (activate) {
      const yes = await confirm(ctx.desktop, {
        title: 'Update the server',
        message: `Install ${name} and switch the server to it?\n\nThe server restarts, which takes a few seconds, and everyone signed in reconnects. The database is copied first, and the build running now stays installed, so you can go back to it from this card or from DSM.`
      });
      if (!yes || ctx.cancelled()) return;
    }
    setBusy(true);
    status.busy(`Installing ${name}…`);
    const stop = watchProgress();
    const result = await crm.installServerVersion(version, activate);
    stop();
    if (ctx.cancelled()) return;
    setBusy(false);
    await finish(result, status);
  }

  async function activate(v: ServerVersionDto): Promise<void> {
    const restore = restoreDatabase.checked;
    if (restore && !v.hasSnapshot) {
      versionStatus.error(
        `There is no copy of the database from when ${v.version} was last left, so it cannot be put back. Untick the box to switch without it.`
      );
      return;
    }
    const older = current ? compareVersions(v.version, current.runningVersion) < 0 : false;
    const yes = await confirm(ctx.desktop, {
      title: older ? 'Go back to an earlier build' : 'Switch builds',
      message:
        `Switch the server to ${v.version}${v.packaged ? ' (packaged)' : ''}?\n\nThe server restarts, which takes a few seconds.` +
        (older && current ? `\n\nAutomatic updates will skip ${current.runningVersion} from now on.` : '') +
        (restore
          ? `\n\nThe database will be put back as ${v.version} last left it. EVERYTHING RECORDED SINCE IS LOST - customers, tickets, time, invoices. The database being replaced is kept as a copy.`
          : '')
    });
    if (!yes || ctx.cancelled()) return;
    setBusy(true);
    versionStatus.busy(`Switching to ${v.version}…`);
    const result = await crm.activateServerVersion(v.packaged ? PACKAGED : v.version, restore);
    if (ctx.cancelled()) return;
    setBusy(false);
    if (result.success) restoreDatabase.checked = false;
    await finish(result, versionStatus);
  }

  async function remove(v: ServerVersionDto): Promise<void> {
    const yes = await confirm(ctx.desktop, {
      title: 'Remove build',
      message: `Remove build ${v.version} from this server?\n\nYou could no longer switch to it without downloading or uploading it again.`
    });
    if (!yes || ctx.cancelled()) return;
    const result = await crm.deleteServerVersion(v.version);
    if (ctx.cancelled()) return;
    if (result.success) {
      await load();
      versionStatus.ok(result.summary);
    } else versionStatus.error(result.error ?? result.summary);
  }

  async function upload(): Promise<void> {
    const files = await pickFiles(false, '.zip');
    if (ctx.cancelled() || files.length === 0) return;
    setBusy(true);
    status.busy(`Uploading ${files[0]!.name}…`);
    try {
      const answer = await uploadFiles(crm.serverUpdateUploadPath(false), files, fraction => {
        if (!ctx.cancelled()) status.busy(`Uploading ${files[0]!.name} (${Math.round(fraction * 100)}%)…`);
      });
      if (ctx.cancelled()) return;
      // A refused build is a 200 carrying its reason, like every action on this card.
      const result = JSON.parse(answer || '{}') as Partial<ServerUpdateActionResult>;
      await load();
      if (result.success) status.ok(result.summary ?? 'Uploaded.');
      else status.error(result.error ?? result.summary ?? 'The build was not installed.');
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }

  async function saveConfig(clearSkipped: boolean): Promise<void> {
    const hours = whole(hoursBox.text);
    const keep = whole(keepBox.text);
    if (hours === null || hours < 1) {
      configStatus.error('Check every must be a whole number of hours, at least 1.');
      return;
    }
    if (keep === null || keep < 1) {
      configStatus.error('Builds to keep must be a whole number, at least 1.');
      return;
    }
    configStatus.busy('Saving…');
    try {
      await crm.saveServerUpdateConfig({
        autoUpdate: autoUpdate.checked,
        feedUrl: feedBox.text.trim(),
        checkHours: hours,
        keepVersions: keep,
        clearSkipped: clearSkipped || null
      });
      if (ctx.cancelled()) return;
      await load();
      configStatus.ok(
        clearSkipped
          ? 'Automatic updates may install the skipped builds again.'
          : autoUpdate.checked
            ? 'Saved. The server checks the feed on that schedule and installs new builds itself.'
            : 'Saved. Automatic updates are off.'
      );
    } catch (error) {
      if (!ctx.cancelled()) configStatus.error(describeError(error));
    }
  }

  /** After an action: if the server is restarting, wait for it and load its interface. */
  async function finish(result: { success: boolean; summary: string; error: string | null; restarting: boolean }, line: StatusLine): Promise<void> {
    if (!result.success) {
      line.error(result.error ?? result.summary);
      await load();
      return;
    }
    if (!result.restarting) {
      await load();
      line.ok(result.summary);
      return;
    }

    line.busy(result.summary);
    const before = current?.runningVersion;
    setBusy(true);
    const started = Date.now();
    await new Promise(resolve => window.setTimeout(resolve, 4000));
    while (!ctx.cancelled() && Date.now() - started < 120_000) {
      try {
        const s = await crm.getServerUpdateStatus();
        if (s.runningVersion !== before || Date.now() - started > 15_000) {
          line.ok(`The server is back, running ${s.runningVersion}. Reloading this page onto it…`);
          window.setTimeout(() => window.location.reload(), 1200);
          return;
        }
      } catch (error) {
        // A refusal means the server is answering again - but this session no longer exists in the
        // database it is answering from, which is what putting an earlier database back does.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          line.ok('The server is back with the earlier database, where this sign-in does not exist. Reloading to sign in…');
          window.setTimeout(() => window.location.reload(), 1200);
          return;
        }
        // Otherwise it is still restarting.
      }
      await new Promise(resolve => window.setTimeout(resolve, 2000));
    }
    if (!ctx.cancelled()) {
      setBusy(false);
      line.error(
        'The server has not come back after two minutes. Check Package Center and Log Center in DSM; the DSM Settings window can switch back to the previous build.'
      );
    }
  }

  unmanaged.visible = false;
  busyLine.visible = false;
  updateButton.visible = false;
  skippedLine.visible = false;
  clearSkippedButton.visible = false;
  availableHeading.visible = false;
  snapshotsHeading.visible = snapshotsNote.visible = false;

  return { card: shell, load };
}
